import { useListPersonas } from '@reelplicate/api-client/personas';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import {
  type Estimate,
  estimate,
  type Issue,
  personaIssues,
  type ResolvedType,
  resolveTypes,
  validateGraph,
} from '@reelplicate/flows';
import { useMemo } from 'react';

import { useEditor } from './editor-store';

export type Analysis = {
  issues: Issue[];
  /** First error per node, for the node's footer. */
  nodeErrors: Map<string, Issue>;
  /** First warning per node, shown when it has no error. */
  nodeWarnings: Map<string, Issue>;
  edgeErrors: Map<string, Issue>;
  /** Output port types, keyed `${nodeId}:${port}`. */
  types: Map<string, ResolvedType>;
  estimate: Estimate;
};

/**
 * Everything the editor derives from the graph, recomputed once per change -
 * plus the checks that need the workspace's personas (who a flow posts for).
 */
export function useAnalysis(): Analysis {
  const graph = useEditor((state) => state.graph);
  const { data: workspaces } = useListWorkspaces();
  const workspaceId = workspaces?.[0]?.id ?? '';
  const { data: personas } = useListPersonas(workspaceId, {
    query: { enabled: Boolean(workspaceId), throwOnError: false },
  });
  return useMemo(() => {
    const issues = [
      ...validateGraph(graph),
      ...(personas ? personaIssues(graph, personas.items) : []),
    ];
    const nodeErrors = new Map<string, Issue>();
    const nodeWarnings = new Map<string, Issue>();
    const edgeErrors = new Map<string, Issue>();
    for (const issue of issues) {
      if (issue.severity === 'warning') {
        if (issue.nodeId && !nodeWarnings.has(issue.nodeId))
          nodeWarnings.set(issue.nodeId, issue);
        continue;
      }
      if (issue.nodeId && !nodeErrors.has(issue.nodeId))
        nodeErrors.set(issue.nodeId, issue);
      if (issue.edgeId && !edgeErrors.has(issue.edgeId))
        edgeErrors.set(issue.edgeId, issue);
    }
    return {
      issues,
      nodeErrors,
      nodeWarnings,
      edgeErrors,
      types: resolveTypes(graph),
      estimate: estimate(graph),
    };
  }, [graph, personas]);
}
